import { type InboxSummary } from '@hvac/contracts';
import { Alert, Card, CardBody, CardHeader, cn, PageHeader, Skeleton } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { inboxSummaryQuery } from '../../shared/api/document-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { takeDroppedFiles } from '../../shared/upload/drop-buffer.js';
import { DocumentList } from './document-list.js';
import { UploadDropzone } from './upload-dropzone.js';
import { UploadQueuePanel } from './upload-queue-panel.js';
import { useUploadQueue } from './use-upload-queue.js';

/**
 * The Inbox.
 *
 * Everything lands here, and the system starts thinking before a person does:
 * the triage line says what arrived and what it turned out to be, the
 * needs-you chips say where the work went, and the stream underneath is the
 * evidence trail. There are no folders anywhere on this page, or anywhere in
 * the product, what used to be filing is now a triage line.
 */

const KIND_LABELS: Record<string, string> = {
  pdf: 'PDFs',
  image: 'photos',
  zip: 'archives',
  csv: 'bank files',
  ofx: 'bank files',
  spreadsheet: 'spreadsheets',
  eml: 'emails',
  text: 'text files',
  unknown: 'unidentified',
  processing: 'still being read',
};

function triageSentence(summary: InboxSummary): string {
  if (summary.arrivedTotal === 0) {
    return `Nothing new in the last ${summary.windowDays} days. Drop anything below, or anywhere in the app.`;
  }
  const parts = Object.entries(summary.byKind)
    .sort((a, b) => b[1] - a[1])
    .map(([kind, count]) => `${count} ${KIND_LABELS[kind] ?? kind}`);
  return `${summary.arrivedTotal} arrived in the last ${summary.windowDays} days: ${parts.join(', ')}.`;
}

export function DocumentsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId;

  const queue = useUploadQueue({ organizationId: organizationId ?? '' });
  const summary = useQuery({
    ...inboxSummaryQuery(organizationId ?? ''),
    enabled: organizationId !== null,
  });

  // Files dropped anywhere in the app land here via the shell's handoff.
  useEffect(() => {
    const dropped = takeDroppedFiles();
    if (dropped !== null && dropped.length > 0) queue.add(dropped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (organizationId === null) {
    return (
      <Alert tone="warning" title="No organisation yet">
        Your account is not a member of an organisation, so there is nowhere to file documents yet.
      </Alert>
    );
  }

  const permissions = session.organizations.find(
    (entry) => entry.organization.id === organizationId,
  )?.permissions;
  const canUpload = permissions?.includes('document:create') ?? false;

  const needsYou = summary.data?.needsYou;
  const chips = [
    { count: needsYou?.review ?? 0, label: 'waiting in review', to: '/review' },
    { count: needsYou?.statements ?? 0, label: 'statements to import', to: '/bank' },
    { count: needsYou?.proposals ?? 0, label: 'matches to confirm', to: '/bank' },
    { count: needsYou?.unknown ?? 0, label: 'need identifying', to: '/review' },
  ].filter((chip) => chip.count > 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Work"
        title="Inbox"
        description={
          summary.data === undefined
            ? 'Everything lands here, and the system sorts out what it is.'
            : triageSentence(summary.data)
        }
      />

      {summary.isPending ? (
        <Skeleton className="h-8 w-2/3" />
      ) : chips.length === 0 ? null : (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <Link
              key={chip.label}
              to={chip.to}
              className={cn(
                'rounded-full border border-border bg-surface px-3 py-1.5 text-sm',
                'text-content transition-colors hover:border-border-strong hover:bg-surface-sunken',
              )}
            >
              <span className="tabular font-semibold">{chip.count}</span>{' '}
              <span className="text-content-muted">{chip.label}</span>
            </Link>
          ))}
        </div>
      )}

      {canUpload ? (
        <UploadDropzone onFiles={(files) => queue.add(files)} />
      ) : (
        <Alert tone="info" title="Read-only access">
          Your role can view documents but not add them.
        </Alert>
      )}

      <UploadQueuePanel queue={queue} />

      <Card>
        <CardHeader
          title="Everything that has arrived"
          description="Stored exactly as it came in, searchable, never filed by hand. The original is the evidence behind every record built from it."
        />
        <CardBody>
          <DocumentList organizationId={organizationId} />
        </CardBody>
      </Card>
    </div>
  );
}
